import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { GetUserByEmail, UpdateUser } from 'actions/user';
import { TextInput } from 'components/form-fields-v2';
import Logo from 'components/logo';
import { Button, Popup } from 'components/quashed';
import {
  isAlphabetCharacters,
  isForeignCharacter,
  removeSpecialCharacters,
  validateEmail,
} from 'helpers';
import { selectUser } from 'selectors';
import { IUser } from 'types';
import styles from './detailsRequiredModal.scss';

const NON_ENGLISH_ERROR_MSG =
  'Please enter names using English characters only.';

const hasForeignName = (firstName: string, lastName: string) =>
  isForeignCharacter(firstName) || isForeignCharacter(lastName);

const nameShow = (user: IUser) =>
  !user.firstName ||
  !user.lastName ||
  !isAlphabetCharacters(user.firstName) ||
  !isAlphabetCharacters(user.lastName);

const shouldShowDetailsRequired = (user: IUser) =>
  user.loaded && (nameShow(user) || !user.email);

const fields = [
  {
    key: 'firstName',
    label: 'First name',
    validation: isAlphabetCharacters,
    show: nameShow,
  },
  {
    key: 'lastName',
    label: 'Last name',
    validation: isAlphabetCharacters,
    show: nameShow,
  },
  {
    key: 'email',
    label: 'Email',
    validation: validateEmail,
    show: (user) => !user.email,
  },
];

const DetailsRequiredModal = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  const { control, formState, handleSubmit, watch } = useForm({
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    },
  });

  const { errors } = formState;
  const { loading } = user;

  const errorMsg = hasForeignName(watch('firstName'), watch('lastName'))
    ? NON_ENGLISH_ERROR_MSG
    : user.error;

  const submit = async (data: any) => {
    const updatedData: Partial<IUser> = {
      firstName: removeSpecialCharacters(data.firstName.trim()),
      lastName: removeSpecialCharacters(data.lastName.trim()),
    };

    if (!user.email) {
      updatedData.email = data.email.trim();
      updatedData.onboardingStep = 1;
      dispatch(GetUserByEmail(updatedData));
    } else {
      dispatch(UpdateUser({ ...user, ...updatedData }));
    }
  };

  const openModal = useMemo(() => shouldShowDetailsRequired(user), [user]);

  return (
    <Popup className={styles.modal} open={openModal} closeOnDimmerClick={false}>
      <form className={styles.container} onSubmit={handleSubmit(submit)}>
        <div className={styles.header}>
          <Logo small={true} />
          <h2>We need one more thing from you</h2>
          <small>Please enter your name {!user.email ? 'and email' : ''}</small>
        </div>
        <div className={styles.questions}>
          {fields.map(
            (f) =>
              f.show(user) && (
                <Controller
                  key={f.key}
                  control={control}
                  name={f.key}
                  rules={{
                    required: true,
                    validate: f.validation,
                  }}
                  render={({ field }) => {
                    return (
                      <TextInput
                        error={!!errors[f.key]}
                        className={styles.field}
                        placeholder={f.label}
                        {...field}
                      />
                    );
                  }}
                />
              ),
          )}
          {errorMsg && <div className={styles.error}>{errorMsg}</div>}
        </div>
        <div className={styles.actions}>
          <Button
            className={styles.submit}
            type="submit"
            text="Continue"
            disabled={!R.isEmpty(errors) || loading}
          />
        </div>
      </form>
    </Popup>
  );
};

export default DetailsRequiredModal;
